// Página PÚBLICA de leitura de régua (E5) — o leiturista externo abre pelo QR
// (/leitura/:token), sem login. Resolve a estação pelo token, registra leitura
// + foto + horário + GPS. Backend: /publico/proposta/{token} (reusa a rota
// pública existente). Mobile-first. Ver docs/estacoes.md.
//
// OFFLINE (2026-08-05): funciona sem sinal depois de aberta 1ª vez com internet.
//  - shell da página cacheado por um service worker ESCOPADO em /leitura/
//    (public/leitura-sw.js — o kill-switch do SW global segue valendo pro app);
//  - dados da estação cacheados em localStorage (o GET falhando offline não
//    bloqueia o formulário);
//  - leitura sem sinal vai pra fila em IndexedDB (utils/leituraOutbox.ts) e é
//    enviada sozinha: pela página (evento 'online' + intervalo) e, no Android,
//    pelo Background Sync mesmo com a página fechada. envio_uuid no backend
//    garante que retry nunca duplica.
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { EntradaLeitura, salvar, contar, drenar, enviarEntrada } from '../utils/leituraOutbox';

interface EstacaoPub {
  codigo: string; nome: string; tipo: string; corpo_hidrico: string | null;
  unidade: string; faixa_min: number | null; faixa_max: number | null;
}

const HD = '#020D7F';
const VERDE = '#0a7f4a';

// Identidade visual do portal de monitoramento (tema.ts/index.css de lá):
// Questrial + Inter, azul-marca #020D7F, ação #0930D0, muted #6A7092, borda
// #E4E7F1. Classes em <style> injetado (única página pública deste app — não
// vale poluir o CSS global do ERP) e com :focus de verdade, que inline não dá.
// Fonts via Google: offline caem no fallback do sistema — cosmético, não quebra.
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Questrial&family=Inter:wght@400;600;700&display=swap');
.lp-raiz { min-height: 100vh; background: linear-gradient(160deg, #EEF2FF 0%, #F7F7F7 55%, #E7ECFD 100%);
  padding: 16px; font-family: Inter, -apple-system, 'Segoe UI', sans-serif; color: #0E1433; }
.lp-cartao { max-width: 460px; margin: 0 auto; background: #fff; border-radius: 10px;
  border: 1px solid #E4E7F1; box-shadow: 0 10px 30px rgba(2,13,127,.10); overflow: hidden; }
.lp-topo { background: linear-gradient(135deg, #020D7F 0%, #0930D0 135%); color: #fff; padding: 18px 20px 15px; }
.lp-marca { font-family: Questrial, Inter, sans-serif; font-size: 12px; letter-spacing: .22em; opacity: .85; }
.lp-titulo { font-family: Questrial, Inter, sans-serif; font-size: 21px; line-height: 1.25; margin-top: 5px; }
.lp-corpo { font-size: 13px; opacity: .9; margin-top: 3px; }
.lp-fila { background: #FFF9EC; color: #B07500; padding: 8px 20px; font-size: 13px; border-bottom: 1px solid #F3E3BC; }
.lp-rotulo { display: block; font-size: 11.5px; font-weight: 600; color: #6A7092;
  text-transform: uppercase; letter-spacing: .06em; margin: 16px 0 6px; }
.lp-campo { width: 100%; padding: 11px 12px; font-size: 16px; border-radius: 6px;
  border: 1px solid #E4E7F1; box-sizing: border-box; font-family: inherit; color: #0E1433;
  background: #fff; transition: border-color .15s, box-shadow .15s; }
.lp-campo:focus { outline: none; border-color: #0930D0; box-shadow: 0 0 0 3px rgba(9,48,208,.12); }
.lp-campo--alerta { border-color: #f59e0b; }
.lp-aviso-faixa { font-size: 12px; color: #B07500; margin-top: 4px; }
.lp-foto { display: block; border: 2px dashed #CBD3EA; border-radius: 8px; text-align: center;
  cursor: pointer; overflow: hidden; color: #6A7092; background: #FBFCFF; transition: border-color .15s; }
.lp-foto:hover, .lp-foto:active { border-color: #02B5DB; }
.lp-erro { color: #A8071A; background: #FFF1F0; border: 1px solid #FFCCC7; padding: 10px 12px;
  border-radius: 6px; font-size: 13px; margin-top: 14px; }
.lp-btn { width: 100%; margin-top: 20px; padding: 13px; font-size: 16px; font-weight: 700;
  font-family: inherit; border: none; border-radius: 6px; background: #0930D0; color: #fff;
  cursor: pointer; box-shadow: 0 2px 10px rgba(9,48,208,.28); }
.lp-btn:disabled { background: #9AA1B8; cursor: wait; box-shadow: none; }
.lp-btn--claro { width: auto; margin-top: 0; padding: 10px 22px; background: #fff; color: #0930D0;
  border: 1px solid #0930D0; box-shadow: none; font-weight: 600; font-size: 15px; }
.lp-rodape { text-align: center; color: #9AA1B8; font-size: 11px; margin-top: 16px;
  font-family: Questrial, Inter, sans-serif; letter-spacing: .12em; text-transform: uppercase; }
`;

// Re-encoda foto (inclusive HEIC do iPhone) pra JPEG reduzido — mesmo motivo do PWA.
async function reencodeJpeg(file: File, maxDim = 2200, q = 0.85): Promise<{ blob: Blob; type: string }> {
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const im = new Image(); im.onload = () => res(im); im.onerror = () => rej(new Error('x')); im.src = url;
    });
    const w0 = img.naturalWidth || img.width, h0 = img.naturalHeight || img.height;
    const s = Math.min(1, maxDim / Math.max(w0, h0));
    const c = document.createElement('canvas');
    c.width = Math.round(w0 * s); c.height = Math.round(h0 * s);
    const ctx = c.getContext('2d'); if (!ctx) throw new Error('x');
    ctx.drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', q));
    if (!blob) throw new Error('x');
    return { blob, type: 'image/jpeg' };
  } catch { return { blob: file, type: file.type || 'image/jpeg' }; }
}


export default function PaginaPublicaEstacao({ token }: { token: string }) {
  const [est, setEst] = useState<EstacaoPub | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [nome, setNome] = useState('');
  const [valor, setValor] = useState('');
  const [quando, setQuando] = useState(() => {
    const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const [obs, setObs] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [pendentes, setPendentes] = useState(0);
  const [guardadaOffline, setGuardadaOffline] = useState(false);
  const drenando = useRef(false);

  async function atualizarFila() {
    try { setPendentes(await contar()); } catch { /* IDB indisponível: segue sem fila */ }
  }
  async function drenarFila() {
    if (drenando.current) return;
    drenando.current = true;
    try { setPendentes(await drenar(API_URL)); } catch { /* offline: tenta na próxima */ }
    finally { drenando.current = false; }
  }

  useEffect(() => {
    // SW escopado SÓ em /leitura/ — cacheia o shell pra página abrir sem sinal.
    // O kill-switch do SW global (escopo /) é um registro separado, não interfere.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/leitura-sw.js', { scope: '/leitura/' }).catch(() => {});
      navigator.serviceWorker.addEventListener('message', (ev) => {
        if (ev.data?.tipo === 'fila') setPendentes(Number(ev.data.restante) || 0);
      });
    }
    setNome(localStorage.getItem('hd-leiturista-nome') || '');
    atualizarFila();
    drenarFila();
    const aoVoltarSinal = () => drenarFila();
    window.addEventListener('online', aoVoltarSinal);
    const timer = window.setInterval(() => { if (navigator.onLine) drenarFila(); }, 45000);
    return () => { window.removeEventListener('online', aoVoltarSinal); window.clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const CACHE_META = `hd-leitura-meta-${token}`;
    axios.get(`${API_URL}/publico/proposta/${token}`)
      .then((r) => {
        const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
        setEst(d);
        try { localStorage.setItem(CACHE_META, JSON.stringify(d)); } catch { /* cheio: segue */ }
      })
      .catch((e) => {
        if (e.response?.status === 404) { setErroCarga('Estação não encontrada. Confira o QR Code.'); return; }
        if (e.response?.status === 410) { setErroCarga('Esta estação está inativa.'); return; }
        // SEM RESPOSTA = offline/erro de rede: usa o cache e deixa o formulário
        // funcionar — a leitura vai pra fila e é validada no envio.
        const cache = localStorage.getItem(CACHE_META);
        if (cache) { try { setEst(JSON.parse(cache)); return; } catch { /* cai no aviso */ } }
        setErroCarga(null);   // formulário genérico ainda funciona offline
      })
      .finally(() => setCarregando(false));
  }, [token]);

  function onFoto(f: File | null) {
    setFoto(f);
    if (fotoPreview) URL.revokeObjectURL(fotoPreview);
    setFotoPreview(f ? URL.createObjectURL(f) : null);
  }

  async function pegarGps(): Promise<{ lat: number | null; lon: number | null }> {
    return new Promise((res) => {
      if (!navigator.geolocation) return res({ lat: null, lon: null });
      navigator.geolocation.getCurrentPosition(
        (p) => res({ lat: p.coords.latitude, lon: p.coords.longitude }),
        () => res({ lat: null, lon: null }), { timeout: 5000, maximumAge: 60000 });
    });
  }

  async function enviar() {
    setErro(null);
    const v = parseFloat(String(valor).replace(',', '.'));
    if (!Number.isFinite(v)) { setErro('Informe a leitura (número).'); return; }
    if (!nome.trim()) { setErro('Informe seu nome.'); return; }
    setEnviando(true);
    try { localStorage.setItem('hd-leiturista-nome', nome.trim()); } catch { /* segue */ }
    try {
      // Monta a entrada COMPLETA antes de tentar a rede: foto já reduzida
      // (JPEG ~2200px) e GPS capturado agora — GPS é satélite, funciona sem
      // sinal de dados; o horário gravado é o do REGISTRO, não o do envio.
      let fotoBlob: Blob | null = null, fotoType: string | null = null;
      if (foto) { const r = await reencodeJpeg(foto); fotoBlob = r.blob; fotoType = r.type; }
      const gps = await pegarGps();
      const entrada: EntradaLeitura = {
        envio_uuid: crypto.randomUUID(), token, valor: v,
        lido_em: new Date(quando).toISOString(), leiturista_nome: nome.trim(),
        observacao: obs.trim() || null, lat: gps.lat, lon: gps.lon,
        fotoBlob, fotoType, criado_em: new Date().toISOString(),
      };
      try {
        await enviarEntrada(API_URL, entrada);
        setGuardadaOffline(false);
        setEnviado(true);
      } catch (e: any) {
        if (e?.definitiva) {
          setErro('O sistema recusou o registro. Confira o QR Code / fale com a Hydrodata.');
          return;
        }
        // Sem sinal: guarda no aparelho e pede Background Sync (Android envia
        // até com a página fechada; nos demais, a página drena ao voltar sinal).
        await salvar(entrada);
        await atualizarFila();
        try {
          const reg = await navigator.serviceWorker?.ready;
          await (reg as any)?.sync?.register('leitura-flush');
        } catch { /* sem Background Sync: a página cuida */ }
        setGuardadaOffline(true);
        setEnviado(true);
      }
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao registrar. Tente de novo.');
    } finally { setEnviando(false); }
  }

  function novaLeitura() {
    setEnviado(false); setValor(''); setObs(''); onFoto(null);
    const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    setQuando(d.toISOString().slice(0, 16));
  }

  const foraFaixa = (() => {
    const v = parseFloat(String(valor).replace(',', '.'));
    if (!est || !Number.isFinite(v)) return false;
    return (est.faixa_min != null && v < est.faixa_min) || (est.faixa_max != null && v > est.faixa_max);
  })();

  return (
    <div className="lp-raiz">
      <style>{CSS}</style>
      <div className="lp-cartao">
        <div className="lp-topo">
          <div className="lp-marca">HYDRODATA · LEITURA DE ESTAÇÃO</div>
          <div className="lp-titulo">{est ? `${est.codigo} — ${est.nome}` : 'Estação'}</div>
          {est?.corpo_hidrico && <div className="lp-corpo">📍 {est.corpo_hidrico}</div>}
        </div>

        {pendentes > 0 && (
          <div className="lp-fila">
            📥 {pendentes} leitura{pendentes > 1 ? 's' : ''} aguardando envio — sai
            {pendentes > 1 ? 'rão' : 'rá'} sozinha{pendentes > 1 ? 's' : ''} quando houver sinal.
          </div>
        )}
        <div style={{ padding: 18 }}>
          {carregando ? <div style={{ textAlign: 'center', color: '#888', padding: 30 }}>Carregando…</div>
          : erroCarga ? <div style={{ color: '#B91626', textAlign: 'center', padding: 20 }}>⚠ {erroCarga}</div>
          : enviado ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 46 }}>{guardadaOffline ? '📥' : '✓'}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: guardadaOffline ? '#b45309' : VERDE, margin: '6px 0' }}>
                {guardadaOffline ? 'Leitura guardada no aparelho!' : 'Leitura registrada!'}
              </div>
              <div style={{ color: '#666', fontSize: 14, marginBottom: 18 }}>
                {guardadaOffline
                  ? 'Sem internet agora — a leitura será enviada automaticamente quando o sinal voltar. Pode fechar esta página.'
                  : 'Obrigado. Os dados já estão no sistema da Hydrodata.'}
              </div>
              <button onClick={novaLeitura} className="lp-btn lp-btn--claro">
                Registrar outra leitura
              </button>
            </div>
          ) : (
            <>
              <label className="lp-rotulo">Seu nome *</label>
              <input className="lp-campo" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Quem está lendo" />

              <label className="lp-rotulo">Leitura na régua ({est?.unidade || 'm'}) *</label>
              <input className={`lp-campo${foraFaixa ? ' lp-campo--alerta' : ''}`} inputMode="decimal" value={valor}
                onChange={(e) => setValor(e.target.value)} placeholder="ex: 1,45" />
              {foraFaixa && <div className="lp-aviso-faixa">⚠ Fora da faixa esperada ({est?.faixa_min ?? '–'} a {est?.faixa_max ?? '–'}). Confira o valor.</div>}

              <label className="lp-rotulo">Data e hora da leitura</label>
              <input className="lp-campo" type="datetime-local" value={quando} onChange={(e) => setQuando(e.target.value)} />

              <label className="lp-rotulo">Foto da régua</label>
              <label className="lp-foto" style={{ padding: fotoPreview ? 0 : 20 }}>
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                  onChange={(e) => onFoto(e.target.files?.[0] || null)} />
                {fotoPreview ? <img src={fotoPreview} alt="foto" style={{ width: '100%', display: 'block' }} />
                  : <span>📷 Tirar foto da régua</span>}
              </label>

              <label className="lp-rotulo">Observação (opcional)</label>
              <textarea className="lp-campo" style={{ minHeight: 56, resize: 'vertical' }} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="ex: chuva forte, água turva…" />

              {erro && <div className="lp-erro">{erro}</div>}

              <button onClick={enviar} disabled={enviando} className="lp-btn">
                {enviando ? 'Enviando…' : 'Enviar leitura'}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="lp-rodape">Hydrodata Engenharia</div>
    </div>
  );
}
